import styled from 'styled-components'
import { motion } from 'framer-motion'

/** Matches invoice detail border-default (~#E4E8EC). */
const borderDefault = '#E4E8EC'

const DEFAULT_ILLUSTRATION_WIDTH = 150

function getAlignItems(isStacked: boolean, isLogo: boolean): string {
  if (isStacked || isLogo) return 'flex-start'
  return 'center'
}

interface Props {
  imgSrc: string
  size?: 'sm' | 'md'
  imgVariant?: 'illustration' | 'logo'
  /** `row` places the image beside content; `stacked` places it above. */
  layout?: 'row' | 'stacked'
  children: React.ReactNode | string
  fillBackground?: boolean
  childrenStyle?: React.CSSProperties
  imgAlt?: string
}

export const ImageMessageBox: React.FC<Props> = ({
  imgSrc,
  size = 'md',
  imgVariant = 'illustration',
  layout = 'row',
  children,
  fillBackground = false,
  childrenStyle,
  imgAlt = ''
}) => {
  const isLogo = imgVariant === 'logo'
  const isStacked = layout === 'stacked'
  const illustrationWidth = size === 'md' ? DEFAULT_ILLUSTRATION_WIDTH : 50

  return (
    <StyledImageMessageBox $fillBackground={fillBackground} $isLogo={isLogo} $isStacked={isStacked}>
      <StyledImg src={imgSrc} alt={imgAlt} $width={illustrationWidth} $isLogo={isLogo} />
      <StyledContent style={childrenStyle}>{children}</StyledContent>
    </StyledImageMessageBox>
  )
}

const StyledImageMessageBox = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 }
})<{ $fillBackground: boolean; $isLogo: boolean; $isStacked: boolean }>`
  display: flex;
  flex-direction: ${({ $isStacked }) => ($isStacked ? 'column' : 'row')};
  flex-wrap: ${({ $isStacked }) => ($isStacked ? 'nowrap' : 'wrap')};
  align-items: ${({ $isStacked, $isLogo }) => getAlignItems($isStacked, $isLogo)};
  /* Gap matches outer padding so image↔content spacing equals edge padding. */
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  color: ${({ theme }) => theme.colors.menuBg};

  ${({ $fillBackground, theme }) =>
    $fillBackground
      ? `
    background: ${theme.colors.neutralGray};
  `
      : `
    border: 1px solid ${borderDefault};
    background: ${theme.colors.defaultHover};
  `}

  .switch-wrapper > * {
    margin-bottom: 0;
  }
`

const StyledContent = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;

  > * + * {
    margin-top: ${({ theme }) => theme.spacing.sm}rem;
  }

  > h4 {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    font-weight: 600;
    line-height: 1.4;
    color: ${({ theme }) => theme.colors.menuBg};
  }

  > p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.metalGray};
  }

  > a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 500;
  }
`

const StyledImg = styled.img<{ $width?: number; $isLogo: boolean }>`
  flex-shrink: 0;

  ${({ $isLogo, theme, $width }) => {
    if ($isLogo) {
      return `
    width: 2rem;
    height: 2rem;
    border-radius: ${theme.borderRadius.sm}rem;
    object-fit: cover;
  `
    }

    const width = $width ?? DEFAULT_ILLUSTRATION_WIDTH

    return `
    flex: 1;
    padding: ${theme.spacing.md}rem;
    max-width: ${width * 1.1}px;
    min-width: ${width * 0.9}px;
  `
  }}
`
